"use client";

import { Smartphone, Bell, CheckCircle } from "lucide-react";
import { useI18n } from "@/lib/i18n/I18nProvider";

export function MobileAppTeaser(): JSX.Element {
  const { t } = useI18n();

  const features = [
    {
      titleKey: "landing.mobileApp.feature1Title",
      descKey: "landing.mobileApp.feature1Desc",
    },
    {
      titleKey: "landing.mobileApp.feature2Title",
      descKey: "landing.mobileApp.feature2Desc",
    },
    {
      titleKey: "landing.mobileApp.feature3Title",
      descKey: "landing.mobileApp.feature3Desc",
    },
    {
      titleKey: "landing.mobileApp.feature4Title",
      descKey: "landing.mobileApp.feature4Desc",
    },
  ];

  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-slate-50">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-block mb-4">
            <span className="px-3 py-1 bg-keiro-green/10 text-keiro-green text-xs font-semibold uppercase tracking-[0.1em] rounded-full">
              {t("landing.mobileApp.badge")}
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-keiro-obsidian mb-4">
            {t("landing.mobileApp.title")}
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {t("landing.mobileApp.subtitle")}
          </p>
        </div>

        {/* Content Grid */}
        <div className="grid md:grid-cols-2 gap-8 items-center">
          {/* Mobile Mockup */}
          <div className="flex justify-center">
            <div className="relative w-48 md:w-64 h-[420px] md:h-[520px] bg-gradient-to-b from-slate-900 to-slate-800 rounded-[2.5rem] shadow-2xl border-8 border-slate-900 overflow-hidden">
              {/* Notch */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-8 bg-slate-900 rounded-b-3xl z-10" />

              {/* Screen Content */}
              <div className="absolute inset-0 pt-10 px-4 flex flex-col">
                {/* Status Bar */}
                <div className="flex justify-between items-center text-white text-xs mb-4">
                  <span>9:41</span>
                  <div className="flex gap-1">
                    <div className="w-1 h-2 bg-white rounded" />
                    <div className="w-1 h-2 bg-white rounded" />
                  </div>
                </div>

                {/* App Header */}
                <div className="bg-gradient-to-r from-keiro-green to-keiro-green px-4 py-3 rounded-lg mb-4 text-white">
                  <h3 className="font-semibold text-sm">
                    {t("landing.mobileApp.appHeaderTitle")}
                  </h3>
                  <p className="text-xs opacity-90">
                    {t("landing.mobileApp.appHeaderSubtitle")}
                  </p>
                </div>

                {/* Payment Cards */}
                <div className="space-y-3 flex-1 overflow-hidden">
                  {/* Card 1 */}
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-xs font-medium text-slate-900">
                          PR-2026-0042
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {t("landing.mobileApp.card1Subtitle")}
                        </p>
                      </div>
                      <span className="text-xs font-semibold text-keiro-green">
                        ₽245K
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1 bg-slate-200 rounded-full overflow-hidden">
                        <div className="w-2/3 h-full bg-amber-500 rounded-full" />
                      </div>
                      <span className="text-xs text-slate-500">2/3</span>
                    </div>
                  </div>

                  {/* Card 2 */}
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-xs font-medium text-slate-900">
                          PR-2026-0043
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {t("landing.mobileApp.card2Subtitle")}
                        </p>
                      </div>
                      <span className="text-xs font-semibold text-keiro-green">
                        ₽128K
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1 bg-slate-200 rounded-full overflow-hidden">
                        <div className="w-1/2 h-full bg-blue-500 rounded-full" />
                      </div>
                      <span className="text-xs text-slate-500">1/2</span>
                    </div>
                  </div>

                  {/* KPI Preview */}
                  <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg p-3 mt-4">
                    <p className="text-xs font-semibold text-slate-700 mb-2">
                      {t("landing.mobileApp.kpiToday")}
                    </p>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-slate-600">
                          RevPAR
                        </span>
                        <span className="text-xs font-semibold text-slate-900">
                          ₽5,240
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-slate-600">
                          {t("landing.mobileApp.kpiBalance")}
                        </span>
                        <span className="text-xs font-semibold text-slate-900">
                          ₽1.2М
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bottom Action */}
                <div className="mt-auto pt-4">
                  <button className="w-full bg-keiro-green text-white text-xs font-semibold py-2 rounded-lg">
                    {t("landing.mobileApp.approveButton")}
                  </button>
                </div>
              </div>

              {/* Home Indicator */}
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-32 h-1 bg-slate-700 rounded-full" />
            </div>

            {/* Floating notification */}
            <div className="absolute bottom-32 -right-12 bg-keiro-green text-white rounded-full p-4 shadow-lg animate-bounce">
              <Bell className="w-6 h-6" />
            </div>
          </div>

          {/* Features List */}
          <div className="space-y-6">
            <div>
              <h3 className="text-2xl font-bold text-keiro-obsidian mb-4">
                {t("landing.mobileApp.featuresTitle")}
              </h3>
              <p className="text-slate-600 leading-relaxed">
                {t("landing.mobileApp.featuresSubtitle")}
              </p>
            </div>

            {/* Features */}
            <ul className="space-y-4">
              {features.map((feature) => (
                <li key={feature.titleKey} className="flex gap-4">
                  <div className="flex-shrink-0 mt-1">
                    <CheckCircle className="w-5 h-5 text-keiro-green" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">
                      {t(feature.titleKey)}
                    </p>
                    <p className="text-sm text-slate-600">
                      {t(feature.descKey)}
                    </p>
                  </div>
                </li>
              ))}
            </ul>

            {/* CTA */}
            <div className="pt-4">
              <button className="inline-flex items-center gap-2 bg-keiro-green hover:bg-keiro-green-dark text-white px-6 py-3 rounded-lg font-semibold transition-colors">
                <Smartphone className="w-5 h-5" />
                {t("landing.mobileApp.notifyMeCta")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
